import type { PickupLocation } from '@otw/models'
import type { ContactCompanyRole } from '@otw/models/leads/contactCompanies'
import type { Contact } from '@otw/models/leads/contacts'

const ROLE_RANK: Record<ContactCompanyRole, number> = {
  owner: 0,
  manager: 1,
  partner: 2,
}

export interface CompanyMemberRef {
  contactId: string
  role: ContactCompanyRole
}

/** Company members sorted by role (owner → manager → partner). */
export const sortCompanyMemberContacts = (
  members: CompanyMemberRef[],
  contacts: Contact[],
): Contact[] =>
  members
    .map((member) => ({
      role: member.role,
      contact: contacts.find((c) => c.id === member.contactId),
    }))
    .filter((entry): entry is { role: ContactCompanyRole; contact: Contact } =>
      Boolean(entry.contact),
    )
    .sort((a, b) => ROLE_RANK[a.role] - ROLE_RANK[b.role])
    .map((entry) => entry.contact)

/**
 * True when this address uses a location that another field on the same form
 * also uses. Editing it must copy the location so the other field stays put.
 */
export const editedLocationIsShared = (
  locationId: string | null | undefined,
  otherLocationIds: Array<string | null | undefined>,
): boolean => {
  const current = locationId?.trim()
  if (!current) return false
  return otherLocationIds.some((id) => id?.trim() === current)
}

export const buildPickupLocationPrefillFromCompany = (
  companyName: string,
  memberContacts: Contact[],
): PickupLocation | null => {
  const company = companyName.trim()
  if (!company && memberContacts.length === 0) return null

  const primary = memberContacts[0]
  const secondary = memberContacts[1]

  return {
    company_name: company || undefined,
    address: '',
    city: '',
    state: '',
    zip: '',
    contact_name: primary?.name?.trim() || '',
    contact_phone: primary?.phone?.trim() || '',
    secondary_contact_name: secondary?.name?.trim() || '',
    secondary_contact_phone: secondary?.phone?.trim() || '',
  }
}

export const contactToPickupContactFields = (
  contact: Contact | null,
): { name: string; phone: string } => ({
  name: contact?.name?.trim() ?? '',
  phone: contact?.phone?.trim() ?? '',
})

const normalizePhoneDigits = (phone: string | undefined): string =>
  (phone ?? '').replace(/\D/g, '')

/** Match a company member to saved pickup-location contact fields (for edit form dropdowns). */
export const findMemberContactForPickupFields = (
  memberContacts: Contact[],
  name: string | undefined,
  phone: string | undefined,
  excludeIds: string[] = [],
): Contact | null => {
  const targetName = name?.trim() ?? ''
  const targetPhone = normalizePhoneDigits(phone)
  if (!targetName && !targetPhone) return null

  for (const contact of memberContacts) {
    if (excludeIds.includes(contact.id)) continue
    const { name: contactName, phone: contactPhoneRaw } = contactToPickupContactFields(contact)
    const contactPhone = normalizePhoneDigits(contactPhoneRaw)

    const nameMatches = Boolean(targetName && contactName && contactName === targetName)
    const phoneMatches = Boolean(targetPhone && contactPhone && contactPhone === targetPhone)

    if (targetName && targetPhone) {
      if (nameMatches && phoneMatches) return contact
      continue
    }
    if (targetName && nameMatches) return contact
    if (targetPhone && phoneMatches) return contact
  }

  return null
}
